import { ISortByQuery } from '@common/api.models';
import { DEFAULT_SELECT_PAGE_SIZE } from '@common/constants';
import { ISearchPropsBase } from '@common/search.services';
import { IFilter } from '@common/searchFilter/searchFilter.models';
import { useProjectId } from '@config/config.hooks';
import { DownloadFile } from '@file/DownloadFile';
import {
  Column,
  createTableCell,
  createTableColumn,
  FormFieldMethods,
  FormSelectPaginate,
  Grid,
  Icon,
  IOnSearchParams,
  IOption,
  IPagedResultsV1,
  ResponsivePaginatedTable,
  Tooltip,
} from '@gcl/react-core';
import { UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import { useEffect } from 'react';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import { AxiosErrorResponse } from '@core/error.models';
import { IProjectDrawingSearchFilter, IProjectDrawingRegisterSearch } from './projectDrawingRegister.models';

interface IFormDrawingSelectProps {
  masterScheduleId: number | undefined;
  formFieldMethods: FormFieldMethods;
  rules?: Object;
  isReadOnly: boolean;
  id: string;
  useFetchSearch: (
    props: ISearchPropsBase<IProjectDrawingSearchFilter>,
    enabled?: boolean,
  ) => UseQueryResult<IPagedResultsV1<IProjectDrawingRegisterSearch>, AxiosErrorResponse>;
  keyExtractor?: (item: IProjectDrawingRegisterSearch) => string | number;
}
export const FormDrawingSelect: React.FunctionComponent<IFormDrawingSelectProps> = ({
  masterScheduleId,
  formFieldMethods,
  rules,
  isReadOnly,
  id,
  useFetchSearch,
  keyExtractor,
}) => {
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_SELECT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>(undefined);
  const projectId = useProjectId();
  const [localFilter, setLocalFilter] = React.useState<IFilter>({ hasRfcDrawingOnly: true, masterScheduleId });
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [optionsMap, setOptionsMap] = React.useState<{ [key: string]: IProjectDrawingRegisterSearch[] }>({});
  const [localOptions, setLocalOptions] = React.useState<IProjectDrawingRegisterSearch[]>([]);

  useEffect(() => {
    setLocalFilter((oldFilter) => {
      return { ...oldFilter, masterScheduleId };
    });
  }, [masterScheduleId]);

  const { data, isLoading } = useFetchSearch(
    {
      pageSize,
      pageNumber,
      projectId,
      ...(sortByQuery || {}),
      filter: Object.keys(localFilter).length > 0 ? localFilter : undefined,
    },
    !!masterScheduleId && masterScheduleId > 0,
  );

  const drawingItems: IOption<IProjectDrawingRegisterSearch>[] = formFieldMethods.watch(id) ?? [];

  useEffect(() => {
    setPageNumber(1);
  }, [JSON.stringify(localFilter)]);

  useDeepCompareEffectNoCheck(() => {
    setLocalOptions(([] as IProjectDrawingRegisterSearch[]).concat.apply([], Object.values(optionsMap)));
  }, [JSON.stringify(optionsMap)]);

  const columns = React.useMemo(() => {
    return [
      ...(isReadOnly
        ? ([] as Column[])
        : [
            createTableColumn({
              Header: '',
              accessor: 'id2',
              disableSortBy: true,
              Cell: createTableCell<string, any>(
                (
                  value: string,
                  item: IProjectDrawingRegisterSearch,
                  index: number,
                  cellExtras: { [key: string]: any },
                ) => {
                  return (
                    <Tooltip overlay="Remove" id={`remove-${index}`} key={`remove-${index}`}>
                      <Icon
                        iconName="Trash"
                        className="gcl-ic-btn-md"
                        onClick={() => {
                          formFieldMethods.setValue(
                            id,
                            ((cellExtras.drawingItems ?? []) as IOption<IProjectDrawingRegisterSearch>[]).filter(
                              (d) => d.data?.id !== item.id,
                            ),
                          );
                        }}
                      />
                    </Tooltip>
                  );
                },
              ),
            } as Column),
          ]),
      createTableColumn({
        Header: 'Drawing / Sketch No.',
        accessor: 'id',
        Cell: createTableCell<string, any>(
          (value: string, item: IProjectDrawingRegisterSearch, index: number, cellExtras: { [key: string]: any }) => {
            return <span>{item.drawingNo}</span>;
          },
        ),
      } as Column),

      {
        Header: 'Drawing Title',
        accessor: 'documentDrawingTitle',
      },
      createTableColumn({
        Header: 'Revision',
        accessor: 'revision',
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IProjectDrawingRegisterSearch, index: number, cellExtras: { [key: string]: any }) => {
            if (item.latestRfcHistory?.projectFileId)
              return (
                <div className="gcl-gems-drawings-download-column">
                  <DownloadFile
                    text={item.latestRfcHistory.revision || item.latestRfcHistory.id.toString()}
                    attachmentId={item.latestRfcHistory.projectFileId}
                    projectId={projectId}
                  />
                </div>
              );
            return <span>{item.latestRfcHistory?.revision}</span>;
          },
        ),
      } as Column),
    ];
  }, [isReadOnly]);

  useEffect(() => {
    if (isLoading || !data || Object.keys(data.items ?? []).length <= 0 || !data.pageNumber) return;
    setHasMore((data.totalCount ?? 0) > (data.pageNumber ?? 0) * (data.pageSize ?? 0));
    setOptionsMap((old) => {
      if ((data.pageNumber ?? 1) === 1) return { [data.pageNumber ?? 1]: data.items ?? [] };
      return { ...old, [data.pageNumber ?? 1]: data.items };
    });
  }, [JSON.stringify(data)]);

  const options = React.useMemo(() => {
    return (localOptions ?? []).map((item) => {
      const key = keyExtractor ? keyExtractor(item) : item.id;
      return { data: item, key, text: `${item.drawingNo} - ${item.documentDrawingTitle}` };
    });
  }, [JSON.stringify(localOptions)]);

  const selectedDrawings = React.useMemo(() => {
    const selectedItems: IProjectDrawingRegisterSearch[] = [];
    drawingItems.forEach((item) => {
      if (item.data) {
        selectedItems.push(item.data);
      }
    });
    return selectedItems;
  }, [JSON.stringify(drawingItems)]);

  return (
    <>
      {!isReadOnly && (
        <FormSelectPaginate<IOption<IProjectDrawingRegisterSearch>>
          rules={rules}
          defaultSelected={drawingItems}
          value={drawingItems ?? []}
          displayFieldName="text"
          placeholder="Select drawings"
          multiple
          isLoading={isLoading}
          hasMore={hasMore}
          formFieldMethods={formFieldMethods}
          options={options}
          id={id}
          renderMenuHeader={() => {
            return (
              <Grid container className="gcl-mb-xs">
                <Grid sm={5} xs={12} className="gcl-pl-md gcl-fs-sm gcl-fw-600 ">
                  Drawing / Sketch No.
                </Grid>
                <Grid sm={6} xs={12} className=" gcl-fs-sm gcl-fw-600">
                  Drawing Title
                </Grid>
                <Grid sm={1} xs={12} className=" gcl-fs-sm gcl-fw-600">
                  Rev.
                </Grid>
              </Grid>
            );
          }}
          renderMenuItem={(item, index) => {
            return (
              <Grid container>
                <Grid sm={5} xs={12} className="gcl-fs-sm gcl-fw-600">
                  {item.data?.drawingNo}
                </Grid>
                <Grid sm={6} xs={12} className=" gcl-fs-sm gcl-fw-600 gcl-txt-break" style={{ whiteSpace: 'pre-wrap' }}>
                  {item.data?.documentDrawingTitle}
                </Grid>
                <Grid sm={1} xs={12} className=" gcl-fs-sm gcl-fw-600">
                  {item.data?.latestRfcHistory?.revision}
                </Grid>
              </Grid>
            );
          }}
          pageSize={pageSize}
          onSearch={(filter: IOnSearchParams) => {
            setLocalFilter((old) => {
              return { ...old, drawingTitle: filter.keyword };
            });
            setPageNumber(filter.pageFilter.skip / pageSize + 1);
          }}
        />
      )}
      {selectedDrawings.length > 0 && (
        <ResponsivePaginatedTable
          allowSelectRow={false}
          columns={columns}
          items={selectedDrawings}
          disablePagination
          cellExtras={{ drawingItems }}
        />
      )}
    </>
  );
};
